import illustrationsAPI from '~/api/illustrations';
import addonsAPI from '~/api/addons';
import _ from 'lodash';

export const emptyIllustration = Object.freeze({
  name: '',
  request_data: {
    create_plan_with_generate: false,
    fees: {
      enable_tiered_charges: false,
      adviser_charge: 0,
      initial_adviser_charge: {
        percent: 0,
        amount: 0,
      },
      adviser_tiered_charges: {
        tiers: [
          {
            from_amount: 0.01,
            to_amount: 1.0,
            rate: 1,
          },
          {
            from_amount: 1.0,
            rate: 1,
          },
        ],
      },
      discretionary_management_charge: 0,
      apply_adviser_charge_across_portfolio: false,
    },
    node_id: null,
    model_id: null,
    payments: {
      initial_payment: 0,
      single_payments: [],
      regular_payments: [],
    },
    fca_number: '',
    start_date: null,
    withdrawals: {
      single_withdrawals: [],
      regular_withdrawals: [],
    },
    wrapper_type: 'ISA',
    wrapper_detail: {
      discretionary: false,
    },
    wrapper_status: null,
    drawdown: {
      drawdown_method: 'FAD',
      funds_not_in_drawdown: 0,
    },
    plan_length_year: 1,
    periods: 12,
    other_platform_holdings: 0,
    period_type: 'Monthly',
    scheme_product_id: null,
    retirement_age: null,
    status: null,
  },
  platform: 'seccl',
  platform_illustration_id: null,
  account_opening_allowed: false,
  is_draft: false,
  modelOrAssets: null,
});
export const emptyGiaData = Object.freeze({
  uuid: null,
  title: null,
  first_name: null,
  last_name: null,
  email: null,
  date_of_birth: null,
  is_jointly_owned: false,
});

export default {
  namespaced: true,

  state: () => ({
    illustrations: [],
    illustration: _.cloneDeep(emptyIllustration),
    secclClient: {},
    illustrationsMeta: {},
  }),

  getters: {
    getIllustration(state) {
      return state.illustration;
    },
    getIllustrations(state) {
      return state.illustrations;
    },
    getSecclClient(state) {
      return state.secclClient;
    },
  },

  actions: {
    async getIllustrations({ state, commit }, { account_uuid, page, per_page = 15 }) {
      const rsp = await illustrationsAPI.getAll(account_uuid, { params: { page: page, include: 'plan', per_page } });
      commit('SET_ILLUSTRATIONS', rsp.data.data);
      commit('SET_ILLUSTRATIONS_META', rsp.data.meta);
      commit('SET_ILLUSTRATION', _.cloneDeep(emptyIllustration)); // clear single one when on fetching list
      return rsp.data.data;
    },

    async getSecclClient({ commit }, { platform, client_uuid }) {
      const secclClient = await addonsAPI
        .secclClient(platform, client_uuid)
        .then((rsp) => {
          return rsp.data.data;
        })
        .catch((error) => {
          return {};
        });

      commit('SET_SECCL_CLIENT', secclClient);
      return secclClient;
    },
    async getIllustration({ commit, state, dispatch }, { illustrationUuid }) {
      let illustration;
      if (state.illustration?.uuid === illustrationUuid) {
        illustration = state.illustration;
      }
      if (state.illustrations.length > 0) {
        illustration = state.illustrations.find(({ uuid }) => uuid === illustrationUuid);
      }
      if (illustration === undefined) {
        const rsp = await illustrationsAPI.get(illustrationUuid);
        illustration = rsp.data.data;
      }

      if (illustration?.request_data?.model_id != null) {
        illustration.modelOrAssets = 'model';
      } else {
        illustration.modelOrAssets = 'assets';
      }

      if (illustration?.request_data !== undefined) {
        if (illustration?.request_data?.create_plan_with_generate === undefined) {
          illustration.request_data.create_plan_with_generate = true;
        }
      }

      if (illustration?.client?.uuid && illustration.client?.uuid !== state.secclClient?.uuid) {
        await dispatch('getSecclClient', {
          platform: illustration.platform,
          client_uuid: illustration.client.uuid,
        });
      }

      if (illustration !== state.illustration) {
        commit('SET_ILLUSTRATION', illustration);
      }

      return illustration;
    },
    async cloneIllustration({ commit, state }) {
      const rsp = await illustrationsAPI.clone(state.illustration.uuid);
      const newIllustration = rsp.data.data;
      commit('SET_ILLUSTRATION', newIllustration);
      commit('SET_ILLUSTRATIONS_PAGE', 1);
      return newIllustration;
    },
    updateIllustrationStatus({ commit }, { illustrationUuid, status }) {
      commit('SET_ILLUSTRATION_STATUS', { illustrationUuid, status });
    },
    async generateIllustration({ commit, state }, { illustrationUuid }) {
      const rsp = await illustrationsAPI.generate(illustrationUuid);
      const updatedIllustration = rsp.data.data;
      commit('SET_ILLUSTRATION', updatedIllustration);

      return updatedIllustration;
    },
    clearIllustration({ commit }) {
      commit('SET_ILLUSTRATION', _.cloneDeep(emptyIllustration));
    },
    async proceedIllustrationToApplication({ commit, state }, { illustrationUuid }) {
      if (illustrationUuid === undefined) {
        return;
      }
      const rsp = await illustrationsAPI.proceed(illustrationUuid);
      const application = rsp.data.data;
      if (state.illustration.uuid === illustrationUuid) {
        commit('SET_ILLUSTRATION', { ...state.illustration, application });
        commit('applications/SET_APPLICATION', application, { root: true });
        commit('applications/SET_SECCL_CLIENT', state.secclClient, { root: true });
      }

      return application;
    },
    async createIllustration({ commit }, illustrationData) {
      const rsp = await illustrationsAPI.create(illustrationData);
      const newIllustration = rsp.data.data;
      commit('SET_ILLUSTRATION', newIllustration);

      return newIllustration;
    },
    async updateIllustration({ commit }, illustrationData) {
      const rsp = await illustrationsAPI.update(illustrationData.uuid, illustrationData);
      const newIllustration = rsp.data.data;
      commit('SET_ILLUSTRATION', newIllustration);

      return newIllustration;
    },
    async deleteIllustration({ commit }, illustrationUuid) {
      await illustrationsAPI.delete(illustrationUuid);
      commit('SET_ILLUSTRATION', _.cloneDeep(emptyIllustration));
    },
  },

  mutations: {
    SET_ILLUSTRATIONS(state, illustrations) {
      state.illustrations = illustrations;
    },
    SET_ILLUSTRATIONS_META(state, meta) {
      state.illustrationsMeta = meta;
    },
    SET_ILLUSTRATIONS_PAGE(state, page) {
      state.illustrationsMeta.current_page = page;
    },
    SET_ILLUSTRATION(state, illustration) {
      if (illustration.request_data) {
        illustration.modelOrAssets = illustration.request_data.model_id != null ? 'model' : 'assets';
      }
      state.illustration = illustration;
      if (illustration.uuid && state.illustrations.length > 0) {
        const illustrationIndex = state.illustrations.findIndex(({ uuid }) => uuid === illustration.uuid);
        if (illustrationIndex === undefined) {
          state.illustrations.push(illustration);
        }
      }
    },
    SET_SECCL_CLIENT(state, secclClient) {
      state.secclClient = secclClient;
    },
    SET_ILLUSTRATION_STATUS(state, { illustrationUuid, status }) {
      if (state.illustration?.uuid === illustrationUuid) {
        state.illustration.status = status;
      }

      if (state.illustrations.length === 0) {
        return;
      }

      const illustrationIndex = state.illustrations.findIndex(({ uuid }) => uuid === illustrationUuid);

      if (illustrationIndex !== undefined && illustrationIndex >= 0) {
        state.illustrations[illustrationIndex].status = status;
      }
    },
  },
};
